import React, {useEffect, useMemo, useRef, useState} from 'react';
import Modal from '../workspace/Modal';
import {WordList} from '../../domain/types';
import {GlobeIcon, LockIcon, PlusIcon, XIcon} from '../Icons';

export interface WordListSubmission {
  name: string;
  description: string;
  isPublic: boolean;
  words: string[];
}

interface WordListFormModalProps {
  isOpen: boolean;
  /** Create and edit ask for exactly the same four things, so they are one dialog. */
  mode: 'create' | 'edit';
  wordlist: WordList | null;
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (submission: WordListSubmission) => void;
}

const controlClass = 'w-full rounded-lg border border-gray-800 bg-gray-950/60 px-3 py-2 text-sm '
  + 'text-gray-100 placeholder-gray-600 focus:border-indigo-500 focus:outline-none '
  + 'focus:ring-1 focus:ring-indigo-500 disabled:opacity-50';

const WordListFormModal: React.FC<WordListFormModalProps> = ({
  isOpen,
  mode,
  wordlist,
  isSaving,
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [words, setWords] = useState<string[]>([]);
  const [newWord, setNewWord] = useState('');
  const newWordInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setName(mode === 'edit' ? wordlist?.name ?? '' : '');
    setDescription(mode === 'edit' ? wordlist?.description ?? '' : '');
    setIsPublic(mode === 'edit' ? wordlist?.isPublic ?? false : false);
    setWords(mode === 'edit' ? [...(wordlist?.words ?? [])] : []);
    setNewWord('');
  }, [isOpen, mode, wordlist]);

  const duplicate = useMemo(() => {
    const trimmed = newWord.trim().toLowerCase();
    return trimmed.length > 0 && words.some(word => word.toLowerCase() === trimmed);
  }, [newWord, words]);

  if (!isOpen || (mode === 'edit' && !wordlist)) {
    return null;
  }

  const addWord = () => {
    const trimmed = newWord.trim();
    if (!trimmed || duplicate) {
      return;
    }
    setWords([...words, trimmed]);
    setNewWord('');
    // Straight back to the input — a list is built by typing twenty words in a row.
    newWordInputRef.current?.focus();
  };

  const canSave = name.trim().length > 0 && !isSaving;

  const submit = () => {
    if (!canSave) {
      return;
    }
    onSubmit({
      name: name.trim(),
      description: description.trim(),
      isPublic,
      words: words.map(word => word.trim()).filter(word => word.length > 0),
    });
  };

  return (
    <Modal
      isOpen
      title={mode === 'create' ? 'New word list' : 'Edit word list'}
      subtitle={
        mode === 'create'
          ? 'Every word here gets cut or beeped out of the conversions that use this list.'
          : undefined
      }
      onClose={isSaving ? () => {} : onClose}
      widthClass="max-w-xl"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="rounded-lg px-3 py-1.5 text-sm text-gray-400 hover:bg-gray-800 hover:text-gray-100
             transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSave}
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white
             hover:bg-indigo-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed
             cursor-pointer"
          >
            {isSaving
              ? (mode === 'create' ? 'Creating…' : 'Saving…')
              : (mode === 'create' ? 'Create list' : 'Save changes')}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="wordlist-name" className="mb-1 block text-xs font-medium text-gray-300">
            Name
          </label>
          <input
            id="wordlist-name"
            type="text"
            value={name}
            onChange={event => setName(event.target.value)}
            placeholder="Common profanity"
            disabled={isSaving}
            className={controlClass}
          />
        </div>

        <div>
          <label htmlFor="wordlist-description" className="mb-1 block text-xs font-medium text-gray-300">
            Description <span className="font-normal text-gray-600">(optional)</span>
          </label>
          <textarea
            id="wordlist-description"
            value={description}
            onChange={event => setDescription(event.target.value)}
            placeholder="What this list is for"
            rows={2}
            disabled={isSaving}
            className={`${controlClass} resize-none`}
          />
        </div>

        <div>
          <span className="mb-1 block text-xs font-medium text-gray-300">Visibility</span>
          <div className="grid grid-cols-2 gap-2">
            <VisibilityOption
              icon={<LockIcon className="w-3.5 h-3.5" />}
              label="Private"
              hint="Only you can see it"
              isSelected={!isPublic}
              disabled={isSaving}
              onSelect={() => setIsPublic(false)}
            />
            <VisibilityOption
              icon={<GlobeIcon className="w-3.5 h-3.5" />}
              label="Public"
              hint="Listed in the marketplace"
              isSelected={isPublic}
              disabled={isSaving}
              onSelect={() => setIsPublic(true)}
            />
          </div>
        </div>

        <div>
          <div className="mb-1 flex items-baseline justify-between">
            <span className="text-xs font-medium text-gray-300">Words</span>
            <span className="text-xs tabular-nums text-gray-600">
              {words.length} word{words.length === 1 ? '' : 's'}
            </span>
          </div>

          <div className="flex gap-2">
            <input
              ref={newWordInputRef}
              type="text"
              value={newWord}
              onChange={event => setNewWord(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addWord();
                }
              }}
              placeholder="Type a word and press Enter"
              aria-label="Add a word"
              disabled={isSaving}
              className={controlClass}
            />
            <button
              type="button"
              onClick={addWord}
              disabled={isSaving || !newWord.trim() || duplicate}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-700 px-3
               text-sm font-medium text-gray-200 hover:border-gray-600 hover:bg-gray-800
               transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              Add
            </button>
          </div>
          {duplicate && (
            <p className="mt-1 text-xs text-amber-300">“{newWord.trim()}” is already in this list.</p>
          )}

          <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-gray-800 bg-gray-950/40">
            {words.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-gray-500">
                No words yet. Matching is case-insensitive, so one spelling is enough.
              </p>
            ) : (
              words.map((word, index) => (
                <div
                  key={index}
                  className="group flex items-center gap-2 border-b border-gray-800/70 px-2 py-1.5
                   last:border-b-0"
                >
                  <span className="w-7 shrink-0 text-right font-mono text-xs tabular-nums text-gray-600">
                    {index + 1}
                  </span>
                  <input
                    type="text"
                    value={word}
                    onChange={(event) => {
                      const updated = [...words];
                      updated[index] = event.target.value;
                      setWords(updated);
                    }}
                    aria-label={`Word ${index + 1}`}
                    disabled={isSaving}
                    className="flex-1 rounded-md border border-transparent bg-transparent px-2 py-1
                     font-mono text-sm text-gray-100 hover:border-gray-800 focus:border-indigo-500
                     focus:bg-gray-950/60 focus:outline-none disabled:opacity-50"
                  />
                  <button
                    type="button"
                    onClick={() => setWords(words.filter((_, i) => i !== index))}
                    disabled={isSaving}
                    aria-label={`Remove ${word}`}
                    className="shrink-0 rounded-md p-1 text-gray-600 opacity-0 transition-colors
                     hover:bg-gray-800 hover:text-red-300 focus:opacity-100 group-hover:opacity-100
                     disabled:cursor-not-allowed cursor-pointer"
                  >
                    <XIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

const VisibilityOption: React.FC<{
  icon: React.ReactNode;
  label: string;
  hint: string;
  isSelected: boolean;
  disabled: boolean;
  onSelect: () => void;
}> = ({icon, label, hint, isSelected, disabled, onSelect}) => (
  <button
    type="button"
    onClick={onSelect}
    disabled={disabled}
    aria-pressed={isSelected}
    className={`rounded-lg border px-3 py-2 text-left transition-colors disabled:opacity-50
     disabled:cursor-not-allowed cursor-pointer ${
  isSelected
    ? 'border-indigo-500/60 bg-indigo-500/[0.08]'
    : 'border-gray-800 bg-gray-950/40 hover:border-gray-700'
  }`}
  >
    <span className={`flex items-center gap-1.5 text-sm font-medium ${
      isSelected ? 'text-indigo-200' : 'text-gray-300'
    }`}>
      {icon}
      {label}
    </span>
    <span className="mt-0.5 block text-xs text-gray-500">{hint}</span>
  </button>
);

export default WordListFormModal;
